import { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Eye } from 'lucide-react';
import clsx from 'clsx';
import Button from './Button';
import { Menu } from '@headlessui/react';

/**
 * Table Component
 * Advanced data table with features:
 * - Column sorting
 * - Row selection
 * - Column visibility toggle
 * - Pagination
 * - Loading skeletons
 * - Empty states
 * - Bulk actions
 * - Accessible with ARIA attributes
 */

export default function Table({
  columns = [],
  data = [],
  loading = false,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  onSort,
  sortColumn,
  sortDirection,
  pagination,
  onPageChange,
  emptyMessage = 'No data available',
  emptyAction,
  bulkActions,
}) {
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const visibleColumns = columns.filter(col => !hiddenColumns.includes(col.key));

  const toggleColumn = (columnKey) => {
    setHiddenColumns(prev =>
      prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(data.map(row => row.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleRowSelect = (rowId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedRows, rowId]);
    } else {
      onSelectionChange?.(selectedRows.filter(id => id !== rowId));
    }
  };

  const handleSort = (columnKey) => {
    if (!onSort) return;

    if (sortColumn === columnKey) {
      onSort(columnKey, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(columnKey, 'asc');
    }
  };

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {(selectable || columns.some(c => c.hideable)) && (
        <div className="flex items-center justify-between px-4 md:px-0">
          {/* Bulk actions */}
          {selectable && selectedRows.length > 0 && bulkActions && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {selectedRows.length} selected
              </span>
              <div className="flex gap-2">
                {bulkActions}
              </div>
            </div>
          )}

          {/* Column visibility */}
          <div className="ml-auto">
            <Menu as="div" className="relative">
              <Menu.Button className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                <Eye className="w-4 h-4" />
                Columns
              </Menu.Button>
              <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 p-2 focus:outline-none z-10">
                {columns.filter(c => c.hideable).map(column => (
                  <Menu.Item key={column.key}>
                    <label className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.includes(column.key)}
                        onChange={() => toggleColumn(column.key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-soft">
        <table className="w-full border-collapse" role="table">
          <thead>
            <tr className="bg-gradient-to-r from-slate-50 to-slate-100/50 border-b-2 border-slate-200" role="row">
              {selectable && (
                <th className="w-12 px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider" role="columnheader">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label="Select all rows"
                  />
                </th>
              )}

              {visibleColumns.map(column => (
                <th
                  key={column.key}
                  className={clsx(
                    'px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider',
                    column.sortable && 'cursor-pointer select-none hover:bg-slate-100/50',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right'
                  )}
                  onClick={() => column.sortable && handleSort(column.key)}
                  role="columnheader"
                  aria-sort={
                    sortColumn === column.key
                      ? sortDirection === 'asc' ? 'ascending' : 'descending'
                      : 'none'
                  }
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && (
                      <span className="text-slate-400">
                        {sortColumn === column.key ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        ) : (
                          <ChevronsUpDown className="w-4 h-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              // Loading skeletons
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-slate-100">
                  {selectable && <td className="px-6 py-4"><div className="skeleton w-4 h-4" /></td>}
                  {visibleColumns.map(column => (
                    <td key={column.key} className="px-6 py-4">
                      <div className="skeleton h-4 w-full max-w-xs" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              // Empty state
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="py-16">
                  <div className="text-center">
                    <p className="text-slate-500 mb-4">{emptyMessage}</p>
                    {emptyAction}
                  </div>
                </td>
              </tr>
            ) : (
              // Data rows
              data.map((row, index) => (
                <tr key={row.id || index} className="border-b border-slate-100 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent transition-colors" role="row">
                  {selectable && (
                    <td className="px-6 py-4 text-sm text-slate-900" role="cell">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        aria-label={`Select row ${row.id}`}
                      />
                    </td>
                  )}

                  {visibleColumns.map(column => (
                    <td
                      key={column.key}
                      className={clsx(
                        'px-6 py-4 text-sm text-slate-900',
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                      role="cell"
                    >
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && !loading && data.length > 0 && (
        <Pagination {...pagination} onPageChange={onPageChange} />
      )}
    </div>
  );
}

/**
 * Pagination Component
 * Server-side pagination controls
 */
function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange }) {
  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 md:px-0">
      <div className="text-sm text-slate-600">
        Showing {startItem} to {endItem} of {totalItems} results
      </div>

      <nav className="flex items-center gap-2" aria-label="Pagination">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          Previous
        </Button>

        <div className="flex gap-1">
          {startPage > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(1)}
              >
                1
              </Button>
              {startPage > 2 && <span className="px-2 py-2 text-slate-400">...</span>}
            </>
          )}

          {pages.map(page => (
            <Button
              key={page}
              variant={page === currentPage ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </Button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 py-2 text-slate-400">...</span>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(totalPages)}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          Next
        </Button>
      </nav>
    </div>
  );
}
